<script lang="ts">
	import {
		Alert,
		ButtonV2,
		Container,
		Input,
		Loader,
		MenuItemList,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import {
		checkUnitDevices,
		deleteUnit,
		getSubUnits,
		offerUnitTransfer,
		removeSubUnit,
		transferUnit,
		transferUnitToNewAccount,
	} from 'src/services/iotu/house-management';
	import { switchUnit } from 'src/services/auth_v2';
	import { currentUnitId, homes, refreshHomes } from 'src/stores/account/home';
	import { get } from 'svelte/store';
	import { user_data, user_settings } from 'src/stores/account/user';
	import {
		pendingUnitTransfers,
		refreshPendingUnitTransfers,
	} from 'src/stores/account/pending_unit_transfers';
	import { validate_email } from 'src/helpers/validators';
	import { migrateIcodesOnUnitChange } from 'src/helpers/installer_helpers';
	import { profiles } from 'src/stores/features/profiles';
	import type { DropdownMenuDataType } from 'src/components/Profiles/DropdownItem.types';
	import { format } from 'svelte-i18n';
	import { MAX_OWNED_HOMES } from 'src/constants';
	import UnitItem from './UnitItem.svelte';
	import UnitDeleteCheckModal from './UnitDeleteCheckModal.svelte';
	import TransferUnitAlreadySharedModal from './TransferUnitAlreadySharedModal.svelte';
	import TransferUnitAlreadyLoggedInModal from './TransferUnitAlreadyLoggedInModal.svelte';
	import TransferHomeInfoModal from './TransferHomeInfoModal.svelte';
	import AddHomeWizardModal from './shared/AddHomeWizardModal.svelte';
	import EditHomeModal from 'src/components/Homes/EditHomeModal.svelte';
	import {
		getFallbackUnitIdForDelete,
		normalizeCheckResponse,
		type UnitDeviceCheck,
	} from './shared/unit-deletion-flow';
	import {
		canAddOwnedHome,
		findOwnedHome,
		getOwnedHomeSlotCount,
		getProfileDisplayName,
	} from 'src/helpers/houses/homes_helpers';

	export let excludeCurrentHome = false;

	let isLoading = false;

	let showAddHomeWizard = false;

	let showEditHomeModal = false;
	let editHomeId: string | null = null;

	let showCheckModal = false;
	let unitToDelete: { id: string; name: string } | null = null;
	let deviceCheck: UnitDeviceCheck | null = null;
	let originalUnitId: string | null = null;

	let showTransferModal = false;
	let showTransferInfoModal = false;
	let unitToTransfer: { id: string; name: string } | null = null;
	let selectedTransferProfile: DropdownMenuDataType | null = null;
	let transferEmail = '';
	let transferEmailError = '';
	let showTransferNewAccountConfirm = false;
	let showAlreadySharedModal = false;
	let showAlreadyLoggedInModal = false;
	let pendingTransferEmail = '';
	let matchedLoggedInProfile: DropdownMenuDataType | null = null;
	let returnUnitIdAfterUnshare: string | null = null;

	$: units = $user_data.available_units ?? {};
	$: allUnitEntries = Object.entries(units) as [string, string][];
	/** Outgoing pending homes stay owned, but are shown only under Pending. */
	$: outgoingPendingUnitIds = new Set(
		($pendingUnitTransfers.outgoing ?? []).map((transfer) => String(transfer.unit_id)),
	);
	$: unitEntries = (
		excludeCurrentHome
			? allUnitEntries.filter(([unitId]) => unitId !== $currentUnitId)
			: allUnitEntries
	).filter(([unitId]) => !outgoingPendingUnitIds.has(unitId));
	$: incomingPendingCount = ($pendingUnitTransfers.incoming ?? []).length;
	$: ownedHomesCount = getOwnedHomeSlotCount(units, incomingPendingCount);
	$: canAddHome = canAddOwnedHome(units, incomingPendingCount);
	$: otherProfiles = $profiles.filter((p) => !p.isSelected);
	$: hasOwnedHomes = Object.keys(units).length > 0;
	$: homeToEdit =
		editHomeId == null ? undefined : findOwnedHome($homes.list, editHomeId, units, $currentUnitId);
	$: if (!showEditHomeModal) {
		editHomeId = null;
	}

	const applyIcodesMigration = (previousUnitCount: number): void => {
		const currentUnitCount = Object.keys($user_data.available_units ?? {}).length;
		const nextIcodes = migrateIcodesOnUnitChange(
			$user_settings?.icodes,
			String($user_data?.user_id),
			previousUnitCount,
			currentUnitCount,
		);
		if (nextIcodes !== null) {
			user_settings.set({ ...$user_settings, icodes: nextIcodes });
		}
	};

	function openAddHomeWizard(): void {
		if (!canAddHome) return;
		showAddHomeWizard = true;
	}

	function onEditUnit(e: CustomEvent<{ unitId: string }>): void {
		editHomeId = e.detail.unitId;
		showEditHomeModal = true;
	}

	async function runDeviceCheckForUnit(id: string, name: string): Promise<void> {
		originalUnitId = $currentUnitId;
		unitToDelete = { id, name };
		deviceCheck = null;
		isLoading = true;
		try {
			const res = await checkUnitDevices(Number(id));
			if (!res.isok) {
				throw new Error('check failed');
			}
			deviceCheck = normalizeCheckResponse(res);
			showCheckModal = true;
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_check_unit_devices', {
					default: 'Could not check this home. Please try again.',
				}),
			});
			unitToDelete = null;
			originalUnitId = null;
		} finally {
			isLoading = false;
		}
	}

	async function onDeleteUnit(e: CustomEvent<{ unitId: string }>): Promise<void> {
		const id = e.detail.unitId;
		await runDeviceCheckForUnit(id, units[id] ?? id);
	}

	function handleCheckCancel(): void {
		showCheckModal = false;
		unitToDelete = null;
		deviceCheck = null;
		originalUnitId = null;
	}

	async function handleCheckSwitchToHome(): Promise<void> {
		if (!unitToDelete) return;
		const targetId = unitToDelete.id;
		showCheckModal = false;
		deviceCheck = null;
		isLoading = true;
		try {
			await switchUnit(targetId);
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_switch_unit_failed', {
					default: 'Could not switch to this home. Please try again.',
				}),
			});
		} finally {
			isLoading = false;
			unitToDelete = null;
			originalUnitId = null;
		}
	}

	async function performDeleteUnitById(unitIdStr: string): Promise<void> {
		const prevCount = allUnitEntries.length;
		const result = await deleteUnit(Number(unitIdStr));
		if (!result.isok) {
			throw new Error($format('error_delete_unit', { default: 'Failed to delete home' }));
		}
		await refreshHomes();
		applyIcodesMigration(prevCount);
		toast.open({
			type: 'success',
			title: $format('success_delete_unit', { default: 'Home deleted successfully' }),
		});
	}

	async function handleCheckDelete(): Promise<void> {
		if (!unitToDelete) return;
		const targetId = unitToDelete.id;
		showCheckModal = false;
		deviceCheck = null;
		isLoading = true;
		try {
			if (get(currentUnitId) === targetId) {
				const availableUnitIds = Object.keys(get(user_data).available_units ?? {});
				const fallbackId = getFallbackUnitIdForDelete({
					targetUnitId: targetId,
					originalUnitId,
					availableUnitIds,
				});
				if (!fallbackId) {
					throw new Error(
						$format('error_delete_unit_no_fallback', {
							default:
								'No other home is available to switch to. You cannot delete the home you are currently using.',
						}),
					);
				}
				await switchUnit(fallbackId);
			}
			await performDeleteUnitById(targetId);
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ?? $format('error_delete_unit', { default: 'Failed to delete home' }),
			});
		} finally {
			isLoading = false;
			originalUnitId = null;
			unitToDelete = null;
		}
	}

	function onTransferUnit(e: CustomEvent<{ unitId: string }>): void {
		const id = e.detail.unitId;
		const home = get(homes).list.find((entry) => entry.id === id && !entry.isShared);
		unitToTransfer = { id, name: home?.name ?? units[id] ?? id };
		selectedTransferProfile = null;
		transferEmail = '';
		transferEmailError = '';
		pendingTransferEmail = '';
		matchedLoggedInProfile = null;
		returnUnitIdAfterUnshare = null;
		showAlreadySharedModal = false;
		showAlreadyLoggedInModal = false;
		showTransferModal = false;
		showTransferInfoModal = true;
	}

	function handleTransferInfoClose(): void {
		showTransferInfoModal = false;
		unitToTransfer = null;
	}

	function handleTransferInfoContinue(): void {
		showTransferInfoModal = false;
		showTransferModal = true;
	}

	async function findSharedSubIdForEmail(email: string): Promise<string | null> {
		const response = await getSubUnits();
		if (!response.isok || !response.data) return null;

		const needle = email.toLowerCase();
		for (const [key, data] of Object.entries(response.data)) {
			if (data?.[0]?.email?.toLowerCase() === needle) {
				return key.replace(/^sub_/, '');
			}
		}
		return null;
	}

	function openAlreadySharedModal(email: string): void {
		pendingTransferEmail = email;
		returnUnitIdAfterUnshare = get(currentUnitId);
		showTransferModal = false;
		showAlreadySharedModal = true;
	}

	function handleTransferSubmit(): void {
		if (selectedTransferProfile) {
			showTransferModal = false;
			executeTransfer();
			return;
		}

		const email = transferEmail.trim();
		if (!email || !unitToTransfer) return;

		if (!validate_email(email)) {
			transferEmailError = $format('input_error_valid_email', {
				default: 'Please enter a valid email!',
			});
			return;
		}

		if (email.toLowerCase() === ($user_data.email ?? '').toLowerCase()) {
			toast.open({
				type: 'danger',
				title: $format('error_transfer_unit_to_own_account', {
					default: 'Cannot transfer a unit to your own account.',
				}),
			});
			return;
		}

		const loggedInProfile = otherProfiles.find(
			(profile) => (profile.email ?? '').toLowerCase() === email.toLowerCase(),
		);
		if (loggedInProfile) {
			matchedLoggedInProfile = loggedInProfile;
			showTransferModal = false;
			showAlreadyLoggedInModal = true;
			return;
		}

		void executeOfferTransfer(email);
	}

	function confirmLoggedInTransfer(): void {
		if (!matchedLoggedInProfile) return;
		selectedTransferProfile = matchedLoggedInProfile;
		matchedLoggedInProfile = null;
		showAlreadyLoggedInModal = false;
		executeTransfer();
	}

	async function confirmUnshareAndTransfer(): Promise<void> {
		if (!pendingTransferEmail || !unitToTransfer) return;

		const email = pendingTransferEmail;
		const transferUnitId = unitToTransfer.id;
		const returnUnitId = returnUnitIdAfterUnshare;
		showAlreadySharedModal = false;
		isLoading = true;

		try {
			// remove_sub_unit is active-home scoped — switch only when user confirms.
			if (get(currentUnitId) !== transferUnitId) {
				await switchUnit(transferUnitId);
			}

			const subId = await findSharedSubIdForEmail(email);
			if (!subId) {
				throw new Error(
					$format('error_transfer_unit_already_shared', {
						default:
							'This home is already shared with that account. Remove shared access first, then try again.',
					}),
				);
			}

			const removeResult = await removeSubUnit(subId);
			if (!removeResult.isok) {
				throw new Error(
					$format('error_remove_sub_unit_before_transfer', {
						default: 'Failed to remove shared access. The transfer offer was not sent.',
					}),
				);
			}

			pendingTransferEmail = '';
			await executeOfferTransfer(email);

			if (returnUnitId && get(currentUnitId) !== returnUnitId) {
				await switchUnit(returnUnitId);
			}
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ??
					$format('error_remove_sub_unit_before_transfer', {
						default: 'Failed to remove shared access. The transfer offer was not sent.',
					}),
			});
			isLoading = false;
		}
	}

	async function executeOfferTransfer(email: string): Promise<void> {
		if (!unitToTransfer) return;

		isLoading = true;
		const uid = Number(unitToTransfer.id);

		try {
			const result = await offerUnitTransfer(uid, email);
			if (!result.isok) {
				const errors = (result.errors ?? {}) as Record<string, unknown>;
				const alreadyPending =
					Boolean(errors.unit_already_has_pending_transfer) ||
					Boolean(errors.already_has_pending_transfer);
				const alreadyShared = Boolean(errors.already_shared);

				if (alreadyShared) {
					openAlreadySharedModal(email);
					return;
				}

				throw new Error(
					alreadyPending
						? $format('error_unit_already_has_pending_transfer', {
								default: 'This unit already has a pending transfer',
							})
						: $format('error_offer_unit_transfer', {
								default: 'Failed to send the transfer offer',
							}),
				);
			}

			await Promise.all([refreshHomes(), refreshPendingUnitTransfers()]);

			toast.open({
				type: 'success',
				title: $format('success_offer_unit_transfer', {
					default: 'Transfer offer sent to {email}',
					values: { email },
				}),
			});
			showTransferModal = false;
			unitToTransfer = null;
			transferEmail = '';
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ??
					$format('error_offer_unit_transfer', {
						default: 'Failed to send the transfer offer',
					}),
			});
		} finally {
			isLoading = false;
		}
	}

	function handleTransferNewAccountDismissed(): void {
		showTransferNewAccountConfirm = false;
		toast.open({
			type: 'success',
			title: $format('success_transfer_unit', {
				default: 'Home transferred successfully',
			}),
		});
		unitToTransfer = null;
		selectedTransferProfile = null;
	}

	async function executeTransfer(): Promise<void> {
		if (!unitToTransfer || !selectedTransferProfile) return;

		isLoading = true;
		const prevCount = allUnitEntries.length;
		const uid = Number(unitToTransfer.id);
		const email = selectedTransferProfile.email ?? '';
		const pass = selectedTransferProfile.pass ?? '';

		try {
			let transferSucceeded = false;

			try {
				const result = await transferUnit(uid, email, pass);
				transferSucceeded = result.isok;
			} catch {
				transferSucceeded = false;
			}

			if (!transferSucceeded) {
				const fallback = await transferUnitToNewAccount(uid, email, pass);
				if (!fallback.isok) {
					throw new Error(
						$format('error_transfer_unit', { default: 'Failed to transfer home' }),
					);
				}
			}

			await refreshHomes();
			applyIcodesMigration(prevCount);

			if (!transferSucceeded) {
				showTransferNewAccountConfirm = true;
			} else {
				toast.open({
					type: 'success',
					title: $format('success_transfer_unit', {
						default: 'Home transferred successfully',
					}),
				});
				unitToTransfer = null;
				selectedTransferProfile = null;
			}
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ??
					$format('error_transfer_unit', { default: 'Failed to transfer home' }),
			});
			unitToTransfer = null;
			selectedTransferProfile = null;
		} finally {
			isLoading = false;
		}
	}

	function onCreateOwnHome(): void {
		if (!canAddHome) {
			toast.open({
				type: 'warning',
				title: $format('alert_owned_homes_limit_reached', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to create a new one.`,
				}),
				duration: 4000,
			});
			return;
		}

		showAddHomeWizard = true;
	}
</script>

{#if !hasOwnedHomes}
	<div class="d-flex flex-column gap-s mt-m justify-content-between align-items-center">
		<Text styleType="body" class="mb-xs">
			{$format('shared_create_own_home_section_title', { default: 'Your Homes' })}
		</Text>
		<ButtonV2
			variant="secondary"
			size="m"
			icon="fas fa-plus"
			loading={isLoading}
			on:click={onCreateOwnHome}
			aria-label={$format('shared_create_own_home_button', {
				default: 'Create your own home',
			})}
		>
			{$format('shared_create_own_home_button', { default: 'Create your own home' })}
		</ButtonV2>
	</div>
	<Alert
		type="info"
		class="mt-s"
		heading={$format('shared_create_own_home_section_info', {
			default: "You don't have your own home yet. Create one to add your own devices.",
		})}
	/>
{:else}
	<div class="mb-m">
		<Text styleType="body-medium">
			{$format('settings_unit_management_title', { default: 'Owned homes' })}
		</Text>
		{#if !canAddHome}
			<Alert
				type="warning"
				class="mt-s"
				heading={$format('alert_owned_homes_limit_reached', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to add a new one.`,
				})}
			/>
		{:else if ownedHomesCount > 0}
			<Text styleType="body-small" class="mt-s text-color-muted">
				{$format('alert_owned_homes_count', {
					values: { count: ownedHomesCount, limit: MAX_OWNED_HOMES },
					default: `Owned homes: ${ownedHomesCount}/${MAX_OWNED_HOMES}`,
				})}
			</Text>
		{/if}
		<ButtonV2
			class="mt-s"
			icon="fas fa-plus"
			variant="secondary"
			size="m"
			loading={isLoading}
			disabled={!canAddHome}
			on:click={openAddHomeWizard}
		>
			{$format('button_add_home', { default: 'Add home' })}
		</ButtonV2>
	</div>
{/if}

{#if isLoading}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{/if}
<div class="pb-m">
	{#each unitEntries as [unitId, unitName], i (unitId)}
		{@const home = $homes.list.find((entry) => entry.id === unitId && !entry.isShared)}
		<div class={i < unitEntries.length - 1 ? 'mb-s' : ''}>
			<UnitItem
				{unitId}
				unitName={home?.name ?? unitName}
				{home}
				isCurrent={unitId === $currentUnitId}
				on:edit={onEditUnit}
				on:delete={onDeleteUnit}
				on:transfer={onTransferUnit}
			/>
		</div>
	{/each}
</div>

<EditHomeModal bind:showModal={showEditHomeModal} home={homeToEdit} />

<AddHomeWizardModal bind:showModal={showAddHomeWizard} variant="v2" />

<UnitDeleteCheckModal
	showModal={showCheckModal}
	unitName={unitToDelete?.name ?? ''}
	ownedCount={deviceCheck?.ownedCount ?? 0}
	sharedCount={deviceCheck?.sharedCount ?? 0}
	useV2={true}
	on:cancel={handleCheckCancel}
	on:switchToHome={handleCheckSwitchToHome}
	on:delete={handleCheckDelete}
/>

<Modal
	showModal={showTransferModal}
	on:close={() => (showTransferModal = false)}
	title={$format('settings_transfer_unit_modal_title', { default: 'Transfer Home' })}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text class="mb-l">
			{$format('settings_transfer_unit_modal_info_new', {
				default: 'Choose a logged-in account or enter an email to transfer "{name}".',
				values: { name: unitToTransfer?.name ?? '' },
			})}
		</Text>

		{#if otherProfiles.length > 0}
			<div class="d-flex flex-direction-column" style="gap: var(--spacing-xs)">
				{#each otherProfiles as profile (profile.email)}
					{@const accAvatar = profile.acc_avatar}
					{@const useColorOnly = accAvatar?.backgroundColor && !accAvatar.image}
					{@const itemAvatar = {
						text: accAvatar?.text,
						icon: accAvatar?.icon,
						backgroundColor: accAvatar?.backgroundColor,
						image: useColorOnly ? '' : accAvatar?.image || 'fak fa-solid-shelly',
						borderStatus: { color: 'color-white' as const },
					}}
					<MenuItemList
						title={getProfileDisplayName(profile.title, profile.email ?? '')}
						subtitle={profile.email}
						avatar={itemAvatar}
						hideActionsSlot
						isSelectable
						selected={selectedTransferProfile?.email === profile.email}
						on:click={() => {
							selectedTransferProfile = profile;
							transferEmail = '';
							transferEmailError = '';
						}}
					/>
				{/each}
			</div>
		{:else}
			<Text class="text-center">
				{$format('settings_transfer_no_profiles_new', {
					default: 'No other logged-in accounts found.',
				})}
			</Text>
		{/if}

		<Text class="mb-m mt-l">
			{$format('settings_transfer_enter_email_title', {
				default: 'Transfer to a new or existing account',
			})}
		</Text>
		<Input
			id="transfer-unit-recipient-email"
			aria-label={$format('settings_sub_account_create_label_email', { default: 'Email' })}
			label={$format('settings_sub_account_create_label_email', { default: 'Email' })}
			bind:value={transferEmail}
			type="email"
			error={transferEmailError}
			placeholder={$format('placeholder_email', { default: 'enter your e-mail' })}
			on:focus={() => (selectedTransferProfile = null)}
			on:input={() => {
				transferEmailError = '';
				selectedTransferProfile = null;
			}}
		/>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			variant="secondary"
			class="mr-s"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={() => (showTransferModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="primary"
			disabled={!selectedTransferProfile && !transferEmail.trim()}
			loading={isLoading}
			aria-label={$format('button_next', { default: 'Next' })}
			on:click={handleTransferSubmit}
		>
			{$format('button_next', { default: 'Next' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showTransferNewAccountConfirm}
	on:close={handleTransferNewAccountDismissed}
	title={$format('settings_transfer_new_account_title', {
		default: 'New Account Created',
	})}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('settings_transfer_new_account_info', {
				default:
					'A new account has been created for "{email}" and the home "{name}" has been assigned to it.',
				values: {
					email: selectedTransferProfile?.email ?? '',
					name: unitToTransfer?.name ?? '',
				},
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 variant="primary" on:click={handleTransferNewAccountDismissed}>
			{$format('button_ok', { default: 'OK' })}
		</ButtonV2>
	</div>
</Modal>

<TransferHomeInfoModal
	bind:showModal={showTransferInfoModal}
	unitId={unitToTransfer?.id ?? ''}
	homeName={unitToTransfer?.name ?? ''}
	useV2={true}
	on:close={handleTransferInfoClose}
	on:continue={handleTransferInfoContinue}
/>

<TransferUnitAlreadySharedModal
	bind:showModal={showAlreadySharedModal}
	homeName={unitToTransfer?.name ?? ''}
	loading={isLoading}
	useV2={true}
	on:confirm={confirmUnshareAndTransfer}
/>

<TransferUnitAlreadyLoggedInModal
	bind:showModal={showAlreadyLoggedInModal}
	email={matchedLoggedInProfile?.email ?? transferEmail}
	loading={isLoading}
	useV2={true}
	on:confirm={confirmLoggedInTransfer}
/>

